import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, Paperclip, X } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import sessionDB from "../../../utils/sessionDB";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import {
  CLAIMS_LIST_PATH,
  CLAIM_CATEGORY,
  CLAIM_STATUS,
  abbreviationFor,
  defaultCurrency,
  formatAmount,
  isSubmitterWritable,
} from "../constants/claimConstants";
import { CLAIM_TYPE } from "../constants/conveyanceConstants";
import { duplicateWindowError, firstOfMonth, toIsoDate } from "../lib/claimRules";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import {
  ClaimBlockedScreen,
  ClaimLoadingScreen,
  useConveyancePolicy,
} from "../components/PolicyGate";
import { Toast } from "../components/Toast";
import { CurrencyAmountField, Field, ReadOnlyValue } from "../components/formControls";

/**
 * Screen 4 — Claim Submission Form: Fixed Monthly Amount (ESS).
 *
 * Two system-populated fields and an optional proof, and that is the whole
 * form: Date is the creation date and Amount comes straight off the active
 * policy's fixed_monthly_amount. Neither is editable by the employee under any
 * circumstance, so both render read-only rather than as disabled inputs.
 *
 * One claim per employee per calendar month. The uniqueness rule lives on a
 * partial index over (tenant, employee, claim_month) in the database; the
 * screen checks the employee's existing claims first so the block names the
 * clashing claim instead of surfacing a constraint violation.
 *
 * Fixed amount does NOT imply auto-approval — the claim is submitted into the
 * same chain as every other category.
 */
export function FixedMonthlyClaimPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { claimId } = useParams();
  const isEdit = Boolean(claimId);

  const {
    loading: policyLoading,
    error: policyError,
    policy,
    currencies,
    isConfigured,
    reload: reloadPolicy,
  } = useConveyancePolicy();

  const empId = useMemo(() => {
    const fromSession = Number(sessionDB.getEmpId());
    if (Number.isFinite(fromSession) && fromSession > 0) return fromSession;
    return getAuthenticatedEmployeeId();
  }, []);

  const [claim, setClaim] = useState(null);
  const [loadingClaim, setLoadingClaim] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [metricId, setMetricId] = useState(null);
  const [proofs, setProofs] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toast, setToast] = useState(null);

  /** Date is the claim's creation date — never backdated or postdated. */
  const claimDate = useMemo(
    () => (claim?.claimDate ? String(claim.claimDate).slice(0, 10) : toIsoDate(new Date())),
    [claim]
  );

  const amount = useMemo(() => {
    if (claim?.totalAmount !== undefined && claim?.totalAmount !== null) {
      return Number(claim.totalAmount);
    }
    const configured = Number(policy?.fixedMonthlyAmount);
    return Number.isFinite(configured) ? configured : 0;
  }, [claim, policy]);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingClaim(true);
    conveyanceClaimService
      .getClaimById(claimId)
      .then(setClaim)
      .catch((error) => {
        console.error("Failed to load claim:", error);
        setLoadError(error.message || t("conveyanceClaim.errorLoadClaim"));
      })
      .finally(() => setLoadingClaim(false));
  }, [claimId, isEdit, t]);

  // Starts on the default currency; an existing claim keeps the metric it
  // was saved with.
  useEffect(() => {
    if (metricId != null) return;
    if (claim?.metricId != null) setMetricId(claim.metricId);
    else if (currencies.length) setMetricId(defaultCurrency(currencies)?.metricId ?? null);
  }, [claim, currencies, metricId]);

  const status = claim?.claimStatus ?? CLAIM_STATUS.DRAFT;
  const readOnly = isEdit && !isSubmitterWritable(status);
  const abbreviation = abbreviationFor(currencies, metricId);

  const handleProofsAdded = (event) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length) setProofs((current) => [...current, ...picked]);
    event.target.value = "";
  };

  const persist = (claimStatus) => {
    setSaving(true);
    setSaveError("");
    const payload = {
      category: CLAIM_CATEGORY.FIXED_MONTHLY,
      claimDate,
      claimMonth: toIsoDate(firstOfMonth(claimDate)),
      totalAmount: amount,
      claimStatus,
      policyId: policy?.policyId ?? null,
      metricId: metricId ?? null,
      empId,
      ...(isEdit ? { modifiedBy: empId } : { createdBy: empId }),
    };

    const request = isEdit
      ? conveyanceClaimService.updateClaim(claimId, payload)
      : conveyanceClaimService.createClaim(payload);

    request
      .then((saved) => {
        setClaim(saved);
        setToast({
          tone: "success",
          message:
            claimStatus === CLAIM_STATUS.DRAFT
              ? t("conveyanceClaim.draftSaved")
              : t("conveyanceClaim.claimSubmitted"),
        });
        // Draft and Submit both end on the claims list, once the toast has
        // been seen.
        setTimeout(() => navigate(CLAIMS_LIST_PATH), 900);
      })
      .catch((error) => {
        console.error("Failed to save claim:", error);
        // This category is always one per calendar month, never a yearly window.
        const duplicate = duplicateWindowError(error);
        setSaveError(
          duplicate
            ? t(duplicate.messageKey, duplicate.values)
            : error.message || t("conveyanceClaim.errorSaveClaim")
        );
      })
      .finally(() => setSaving(false));
  };

  /* ------------------------------------------------------------- gating */

  if (policyLoading || loadingClaim) return <ClaimLoadingScreen />;

  if (policyError) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.policyUnavailableTitle")}
        message={policyError}
        onRetry={reloadPolicy}
      />
    );
  }

  if (!isConfigured) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.noPolicyTitle")}
        message={t("conveyanceClaim.noPolicyMessage")}
        onRetry={reloadPolicy}
      />
    );
  }

  if (policy.claimType !== CLAIM_TYPE.FIXED_MONTHLY) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.categoryUnavailableTitle")}
        message={t("conveyanceClaim.fixedMonthlyUnavailableMessage")}
      />
    );
  }

  if (loadError) {
    return <ClaimBlockedScreen title={t("conveyanceClaim.errorLoadClaim")} message={loadError} />;
  }

  return (
    <AppLayout className="space-y-5">
      {toast ? (
        <Toast tone={toast.tone} onDone={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}

      <PageBackLink to={CLAIMS_LIST_PATH} />

      <div>
        <h1 className={ui.pageTitle}>{t("conveyanceClaim.fixedMonthlyFormTitle")}</h1>
      </div>

      {status === CLAIM_STATUS.SENT_BACK ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-bold text-amber-900">{t("conveyanceClaim.sentBackTitle")}</p>
            <p className="mt-0.5 text-[12px] text-amber-800">{t("conveyanceClaim.sentBackMessage")}</p>
          </div>
        </div>
      ) : null}

      {saveError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{saveError}</p>
        </div>
      ) : null}

      <ContentCard title={t("conveyanceClaim.claimDetail")} bodyClassName="p-4 sm:p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("conveyanceClaim.date")} hint={t("conveyanceClaim.dateAutoHint")}>
            <ReadOnlyValue>{claimDate}</ReadOnlyValue>
          </Field>

          <Field label={t("conveyanceClaim.amount")} hint={t("conveyanceClaim.fixedAmountHint")}>
            <CurrencyAmountField
              value={amount}
              currencies={currencies}
              metricId={metricId}
              onMetricChange={setMetricId}
              disabled={readOnly}
              readOnly
            />
          </Field>
        </div>

        {/* Proof is optional for this category (PRD C.8). */}
        <Field label={t("conveyanceClaim.proofs")} hint={t("conveyanceClaim.proofOptionalHint")}>
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={cn(
                "inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-[var(--color-text-border)] bg-white text-sm font-semibold text-foreground hover:bg-gray-50 transition",
                readOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              )}
            >
              <Paperclip className="h-4 w-4" />
              {t("conveyanceClaim.attachProof")}
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                disabled={readOnly}
                onChange={handleProofsAdded}
                className="sr-only"
              />
            </label>
            {proofs.map((proof, index) => (
              <span
                key={`${proof.name}-${index}`}
                className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-lg bg-[var(--color-workspace)]/50 text-[11px] font-semibold text-foreground max-w-[220px]"
              >
                <span className="truncate">{proof.name}</span>
                <button
                  type="button"
                  onClick={() => setProofs((current) => current.filter((_, i) => i !== index))}
                  aria-label={t("formActions.remove")}
                  className="h-5 w-5 grid place-items-center rounded text-[var(--color-text-secondary)] hover:text-[var(--color-error)] transition cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        </Field>

        <div className="flex items-start gap-2.5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-sky-600" />
          <p className="text-[12px] text-sky-900">{t("conveyanceClaim.proofsNotStoredNotice")}</p>
        </div>
      </ContentCard>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[var(--color-text-border)]/70 bg-white p-4 sm:p-5">
        <div>
          <p className={ui.sectionLabel}>{t("conveyanceClaim.claimTotal")}</p>
          <p className="text-xl font-extrabold text-foreground">{formatAmount(amount, abbreviation)}</p>
          <div className="mt-1">
            <ClaimStatusBadge status={status} />
          </div>
        </div>

        {readOnly ? (
          <p className="text-[11px] text-[var(--color-text-secondary)] max-w-[360px]">
            {t("conveyanceClaim.readOnlyNotice")}
          </p>
        ) : (
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => persist(CLAIM_STATUS.DRAFT)}
              disabled={saving}
              className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("conveyanceClaim.saveDraft")}
            </button>
            <button
              type="button"
              onClick={() => persist(CLAIM_STATUS.SUBMITTED)}
              disabled={saving || !(amount > 0)}
              className={ui.primaryBtn}
            >
              {saving ? t("localConveyance.saving") : t("conveyanceClaim.submitClaim")}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default FixedMonthlyClaimPage;
